import { DeleteIcon } from '@chakra-ui/icons'
import { Button, Flex, IconButton, Text } from '@chakra-ui/react'
import { DropdownOptionItem, FieldTypes, getUniqueRandomId, TextField } from '@hb/shared'
import { StandaloneInput } from '../../forms/Input'
import { ToggleButton, ToggleContainer } from '../ToolbarComponents'

const optionLabelField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Option label',
  optional: true,
}

// The add / remove / rename list of radio options (no direction control). Reused by the creation-popover
// editor below and by the toolbar's "Options" popover (RadioInputToolbar). Options are flat `{ text, id }`
// items; `id` is a stable generated value so relabeling an option never changes the stored form value.
export const RadioOptionsList: React.FC<{
  options: DropdownOptionItem[]
  onChange: (options: DropdownOptionItem[]) => void
}> = ({ options, onChange }) => {
  const updateOption = (id: string, text: string) =>
    onChange(options.map(o => (o.id === id ? { ...o, text } : o)))
  const removeOption = (id: string) => onChange(options.filter(o => o.id !== id))
  const addOption = () =>
    onChange([...options, { id: getUniqueRandomId(options.map(o => o.id)), text: '' }])

  return (
    <Flex flexFlow="column" gap={1} w="100%">
      {options.map(option => (
        <Flex key={option.id} align="center" gap={1} w="100%">
          <Flex flex={1} minW={0}>
            <StandaloneInput
              theme="detailed"
              field={optionLabelField}
              value={option.text}
              onChange={text => updateOption(option.id, text ?? '')}
            />
          </Flex>
          <IconButton
            size="xs"
            variant="ghost"
            colorScheme="red"
            aria-label="Remove option"
            icon={<DeleteIcon />}
            onClick={() => removeOption(option.id)}
          />
        </Flex>
      ))}
      <Button size="xs" variant="outline" alignSelf="flex-start" onClick={addOption}>
        + Add option
      </Button>
    </Flex>
  )
}

// Direction toggle + options list stacked — used by the creation popover, where vertical space exists.
export const RadioOptionsEditor: React.FC<{
  options: DropdownOptionItem[]
  onChange: (options: DropdownOptionItem[]) => void
  direction: 'row' | 'column'
  onDirectionChange: (direction: 'row' | 'column') => void
}> = ({ options, onChange, direction, onDirectionChange }) => (
  <Flex flexFlow="column" gap={2} w="100%">
    <Flex align="center" justify="space-between" gap={2}>
      <Text fontSize="sm" fontWeight={600} opacity={0.8}>
        Options
      </Text>
      <ToggleContainer>
        <ToggleButton active={direction === 'row'} onClick={() => onDirectionChange('row')}>
          Row
        </ToggleButton>
        <ToggleButton active={direction === 'column'} onClick={() => onDirectionChange('column')}>
          Column
        </ToggleButton>
      </ToggleContainer>
    </Flex>
    <RadioOptionsList options={options} onChange={onChange} />
  </Flex>
)
